import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, Truck, ArrowRight, Flower2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate } from "@/components/status";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

export default function Home() {
  const { store } = useAuth();
  const navigate = useNavigate();
  const [sched, setSched] = useState(null);
  const [decks, setDecks] = useState([]);

  useEffect(() => {
    api.schedule().then(setSched).catch(() => {});
    api.decks().then((d) => setDecks(d.filter((x) => x.is_visible !== false))).catch(() => {});
  }, []);

  const cw = sched?.current_window;
  const storeDays = store?.delivery_days || [];

  return (
    <div className="animate-fade-up space-y-5">
      {/* delivery days */}
      <div className="flex flex-wrap items-center gap-2">
        <CalendarClock className="h-4 w-4 text-[#EBBAD1]" />
        <span className="text-sm font-medium text-white/90">Metcash deliveries:</span>
        {storeDays.length ? storeDays.map((d) => (
          <span key={d} className="rounded-full bg-[#EBBAD1] px-2.5 py-0.5 text-[11px] font-bold uppercase text-[#28282E]">{d}</span>
        )) : <span className="text-sm text-white/78">set by Hasfarm</span>}
      </div>

      {/* next cutoff hero */}
      {cw && (
        <div data-testid="home-cutoff-hero" className="rounded-3xl bg-gradient-to-b from-[#28282E] to-[#D46117] p-5 shadow-lg">
          <span className="inline-block rounded-full bg-black/20 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide">Next order cutoff</span>
          <div className="mt-3 font-serif text-4xl font-extrabold">{cw.cutoff_label}</div>
          <div className="mt-3 flex items-center gap-2 text-sm text-white/90">
            <Truck className="h-4 w-4" /> Delivered {cw.delivery_label}
            {cw.delivery_dates?.length ? ` — ${cw.delivery_dates.map(fmtDate).join(", ")}` : ""}
          </div>
          <p className="mt-2 text-sm font-medium text-white/90">Place your order before the cutoff to be included in this delivery.</p>
        </div>
      )}

      {/* discover */}
      <div>
        <h2 className="mb-2 font-serif text-xl font-bold">Discover</h2>
        <div className="space-y-3">
        {decks.map((d) => {
          const link = d.key === "friday_specials" ? "/store/catalogue?view=specials"
            : d.key === "events" ? "/store/catalogue?view=events"
            : (d.key === "flower_care" || d.key === "merchandise_ideas") ? `/store/content/${d.key}`
            : "/store/catalogue";
          return (
            <button key={d.id} data-testid={`home-deck-${d.key}`} onClick={() => navigate(link)}
              className="relative block w-full overflow-hidden rounded-2xl text-left">
              <img src={d.image_url} alt={d.title} className="h-40 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#28282E]/90 via-[#28282E]/50 to-transparent" />
              <div className="absolute inset-0 flex flex-col justify-end p-4">
                <div className="text-[10px] font-bold uppercase tracking-wide text-[#EBBAD1]">{d.subtitle}</div>
                <div className="font-serif text-2xl font-extrabold">{d.title}</div>
                <p className="mt-1 line-clamp-2 max-w-md text-xs font-medium text-white/90">{d.body}</p>
                <span className="mt-3 inline-flex w-fit items-center gap-1 rounded-full bg-[#28282E] px-4 py-2 text-sm font-bold">
                  {d.key === "friday_specials" ? "View specials" : d.key === "events" ? "Browse events" : (d.key === "flower_care" || d.key === "merchandise_ideas") ? "View" : "Start ordering"} <ArrowRight className="h-4 w-4" />
                </span>
              </div>
            </button>
          );
        })}
        </div>
      </div>

      <button data-testid="home-browse-catalogue" onClick={() => navigate("/store/catalogue")}
        className="flex w-full items-center gap-3 rounded-2xl bg-white/10 p-4 text-left hover:bg-white/15">
        <span className="grid h-10 w-10 place-items-center rounded-full bg-[#EBBAD1] text-[#28282E]"><Flower2 className="h-5 w-5" /></span>
        <span className="flex-1 font-semibold">Browse full flower catalogue</span>
        <ArrowRight className="h-5 w-5 text-[#EBBAD1]" />
      </button>
    </div>
  );
}
